import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSiteSettings, type SiteSettings } from '../../services/adminApi';

interface Props {
  site: SiteSettings;
}

export function SiteSettingsForm({ site }: Props) {
  const [name, setName] = useState(site.name);
  const [timezone, setTimezone] = useState(site.timezone ?? '');
  const [capacityMwh, setCapacityMwh] = useState(
    site.capacity_mwh === null ? '' : String(site.capacity_mwh)
  );
  const [lat, setLat] = useState(site.location ? String(site.location.lat) : '');
  const [lon, setLon] = useState(site.location ? String(site.location.lon) : '');
  const [street, setStreet] = useState(site.address?.street ?? '');
  const [city, setCity] = useState(site.address?.city ?? '');
  const [stateRegion, setStateRegion] = useState(site.address?.state ?? '');
  const [zip, setZip] = useState(site.address?.zip ?? '');
  const [batteryCount, setBatteryCount] = useState(String(site.battery_count));
  const [inverterCount, setInverterCount] = useState(String(site.inverter_count));
  const [solarCount, setSolarCount] = useState(
    String((site.metadata?.solar_count as number | undefined) ?? 0)
  );
  const [generatorCount, setGeneratorCount] = useState(
    String((site.metadata?.generator_count as number | undefined) ?? 0)
  );
  const [meterCount, setMeterCount] = useState(
    String((site.metadata?.meter_count as number | undefined) ?? 0)
  );
  // Operator maintenance (djin-cloud#795). The box follows the latest loaded status until the admin
  // changes it; only then is it "dirty", and only a dirty box sends `status`. A refetch after another
  // admin declares or clears maintenance therefore updates the box instead of arming a stale write.
  const liveMaintenance = site.status === 'maintenance';
  const [maintenance, setMaintenance] = useState(liveMaintenance);
  const [maintenanceDirty, setMaintenanceDirty] = useState(false);
  useEffect(() => {
    if (!maintenanceDirty) setMaintenance(liveMaintenance);
    else if (maintenance === liveMaintenance) setMaintenanceDirty(false);
  }, [liveMaintenance, maintenance, maintenanceDirty]);
  const [error, setError] = useState('');
  const [successNote, setSuccessNote] = useState('');

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) {
        throw new Error('Site name is required');
      }

      const latNum = lat === '' ? null : Number(lat);
      const lonNum = lon === '' ? null : Number(lon);
      const capacityNum = capacityMwh === '' ? null : Number(capacityMwh);

      if (capacityMwh !== '' && (Number.isNaN(capacityNum!) || capacityNum! < 0)) {
        throw new Error('Project size must be a non-negative number');
      }
      if ((lat === '') !== (lon === '')) {
        throw new Error('Provide both latitude and longitude, or leave both empty');
      }
      if (
        latNum !== null &&
        (Number.isNaN(latNum) || latNum < -90 || latNum > 90 || Number.isNaN(lonNum!) || lonNum! < -180 || lonNum! > 180)
      ) {
        throw new Error('Latitude must be -90..90 and longitude -180..180');
      }

      const addressObj: { street?: string; city?: string; state?: string; zip?: string } = {};
      if (street) addressObj.street = street;
      if (city) addressObj.city = city;
      if (stateRegion) addressObj.state = stateRegion;
      if (zip) addressObj.zip = zip;
      const hasAddress = Object.keys(addressObj).length > 0;

      const payload = {
        name: name.trim(),
        timezone: timezone.trim() || null,
        capacity_mwh: capacityNum,
        battery_count: Math.max(0, Math.floor(Number(batteryCount) || 0)),
        inverter_count: Math.max(0, Math.floor(Number(inverterCount) || 0)),
        address: hasAddress ? addressObj : null,
        location: latNum !== null ? { lat: latNum, lon: lonNum as number } : null,
        metadata: {
          ...(site.metadata ?? {}),
          solar_count: Math.max(0, Math.floor(Number(solarCount) || 0)),
          generator_count: Math.max(0, Math.floor(Number(generatorCount) || 0)),
          meter_count: Math.max(0, Math.floor(Number(meterCount) || 0)),
        },
        // Sent only while the admin's change to the maintenance box is unsaved (#795).
        ...(maintenanceDirty ? { status: maintenance ? ('maintenance' as const) : null } : {}),
      };

      return updateSiteSettings(site.site_id, payload);
    },
    onSuccess: (res) => {
      setError('');
      setSuccessNote(
        res.changed.length
          ? `Saved — ${res.changed.length} field${res.changed.length === 1 ? '' : 's'} changed`
          : 'No changes to save'
      );
      queryClient.invalidateQueries({ queryKey: ['site-settings', site.site_id] });
      queryClient.invalidateQueries({ queryKey: ['project', site.site_id] });
      queryClient.invalidateQueries({ queryKey: ['site-audit-log', site.site_id] });
    },
    onError: (err: any) => {
      setSuccessNote('');
      setError(err.response?.data?.error || err.message || 'Failed to save');
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
      className="bg-ems-bg-card rounded-2xl border border-ems-border p-6 space-y-4"
    >
      {error && (
        <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">
          {error}
        </div>
      )}
      {successNote && (
        <div className="p-3 rounded-lg bg-ems-green/10 border border-ems-green/20 text-ems-green text-sm">
          {successNote}
        </div>
      )}

      <Field label="Site Name" value={name} onChange={setName} required />
      {site.site_code && (
        <div>
          <label className="block text-sm font-medium text-ems-text-primary mb-1">
            Site Code (read-only)
          </label>
          <input
            type="text"
            value={site.site_code}
            disabled
            className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-muted bg-ems-bg/50 cursor-not-allowed"
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Timezone" value={timezone} onChange={setTimezone} placeholder="UTC" />
        <Field
          label="Project Size (MWh)"
          value={capacityMwh}
          onChange={setCapacityMwh}
          type="number"
          step="0.01"
          min="0"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Latitude" value={lat} onChange={setLat} type="number" step="any" />
        <Field label="Longitude" value={lon} onChange={setLon} type="number" step="any" />
      </div>

      <fieldset className="space-y-3 border border-ems-border rounded-lg p-3">
        <legend className="px-2 text-xs uppercase tracking-wider text-ems-text-muted">
          Address
        </legend>
        <Field label="Street" value={street} onChange={setStreet} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="City" value={city} onChange={setCity} />
          <Field label="State" value={stateRegion} onChange={setStateRegion} />
          <Field label="ZIP" value={zip} onChange={setZip} />
        </div>
      </fieldset>

      <fieldset className="space-y-3 border border-ems-border rounded-lg p-3">
        <legend className="px-2 text-xs uppercase tracking-wider text-ems-text-muted">
          Equipment Counts
        </legend>
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Batteries"
            value={batteryCount}
            onChange={setBatteryCount}
            type="number"
            min="0"
          />
          <Field
            label="Inverters"
            value={inverterCount}
            onChange={setInverterCount}
            type="number"
            min="0"
          />
          <Field
            label="Solar"
            value={solarCount}
            onChange={setSolarCount}
            type="number"
            min="0"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Generators"
            value={generatorCount}
            onChange={setGeneratorCount}
            type="number"
            min="0"
          />
          <Field
            label="Meters"
            value={meterCount}
            onChange={setMeterCount}
            type="number"
            min="0"
          />
        </div>
      </fieldset>

      <fieldset className="space-y-2 border border-ems-border rounded-lg p-3">
        <legend className="px-2 text-xs uppercase tracking-wider text-ems-text-muted">
          Operational Status
        </legend>
        <label className="flex items-start gap-3 text-sm text-ems-text-primary">
          <input
            type="checkbox"
            checked={maintenance}
            onChange={(e) => {
              setMaintenance(e.target.checked);
              setMaintenanceDirty(true);
            }}
            className="mt-0.5"
          />
          <span>
            Maintenance mode
            <span className="block text-xs text-ems-text-muted">
              Declares planned work. The site stays in maintenance until you clear it here; telemetry
              and the offline check will not change it. Clearing re-derives the status from the
              site&apos;s equipment.
            </span>
          </span>
        </label>
        <p className="text-xs text-ems-text-muted">{`Current status: ${site.status ?? 'unknown'}`}</p>
      </fieldset>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  placeholder,
  step,
  min,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  step?: string;
  min?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ems-text-primary mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        step={step}
        min={min}
        className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-primary bg-ems-bg outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
      />
    </div>
  );
}
